/**
 * Mortgage Calculation Utilities
 * Handles all mortgage-related calculations with support for different payment frequencies
 */

/**
 * Calculate periodic payment amount based on payment frequency
 * Formula: P = M * [r(1+r)^n] / [(1+r)^n - 1]
 * where P = principal, r = periodic rate, n = number of payments
 * 
 * @param {number} mortgageAmount - Total mortgage amount
 * @param {number} annualInterestRate - Annual interest rate as percentage (e.g., 5.5 for 5.5%)
 * @param {number} amortizationYears - Amortization period in years
 * @param {string} paymentFrequency - 'monthly', 'biweekly', or 'weekly'
 * @returns {Object} - { periodicPayment, annualPayment, paymentsPerYear, periodicRate, totalPayments }
 */
export const calculateMortgagePayment = (mortgageAmount, annualInterestRate, amortizationYears, paymentFrequency = 'monthly') => {
  if (!mortgageAmount || mortgageAmount <= 0) {
    return {
      periodicPayment: 0,
      annualPayment: 0,
      paymentsPerYear: 12,
      periodicRate: 0,
      totalPayments: 0
    };
  }

  // Determine payment frequency parameters
  // In Canada, interest rates are compounded semi-annually
  // To convert annual rate to periodic rate: (1 + r/2)^(2/n) - 1
  // where r = annual rate (as decimal), n = number of payments per year
  let paymentsPerYear;
  let periodicRate;
  let totalPayments;
  
  const annualRateDecimal = annualInterestRate / 100; // Convert percentage to decimal

  switch (paymentFrequency) {
    case 'weekly':
      paymentsPerYear = 52;
      // Canadian semi-annual compounding: (1 + r/2)^(2/52) - 1
      periodicRate = Math.pow(1 + annualRateDecimal / 2, 2 / 52) - 1;
      totalPayments = amortizationYears * 52;
      break;
    case 'biweekly':
      paymentsPerYear = 26;
      // Canadian semi-annual compounding: (1 + r/2)^(2/26) - 1
      periodicRate = Math.pow(1 + annualRateDecimal / 2, 2 / 26) - 1;
      totalPayments = amortizationYears * 26;
      break;
    case 'monthly':
    default:
      paymentsPerYear = 12;
      // Canadian semi-annual compounding: (1 + r/2)^(2/12) - 1
      periodicRate = Math.pow(1 + annualRateDecimal / 2, 2 / 12) - 1;
      totalPayments = amortizationYears * 12;
      break;
  }

  // Calculate periodic payment
  let periodicPayment = 0;
  if (periodicRate > 0) {
    // Standard amortization formula
    periodicPayment = mortgageAmount * 
      (periodicRate * Math.pow(1 + periodicRate, totalPayments)) / 
      (Math.pow(1 + periodicRate, totalPayments) - 1);
  } else {
    // If interest rate is 0, simply divide principal by number of payments
    periodicPayment = mortgageAmount / totalPayments;
  }

  const annualPayment = periodicPayment * paymentsPerYear;

  return {
    periodicPayment,
    annualPayment,
    paymentsPerYear,
    periodicRate,
    totalPayments
  };
};

/**
 * Round to nearest cent (2 decimal places)
 */
const roundToCent = (value) => {
  return Math.round(value * 100) / 100;
};

/**
 * Calculate remaining mortgage balance after n payments using period-by-period simulation
 * This matches bank calculators exactly by rounding at each step
 * 
 * @param {number} mortgageAmount - Initial mortgage amount
 * @param {number} annualInterestRate - Annual interest rate as percentage
 * @param {number} amortizationYears - Amortization period in years
 * @param {string} paymentFrequency - 'monthly', 'biweekly', or 'weekly'
 * @param {number} paymentsMade - Number of payments already made
 * @returns {number} - Remaining mortgage balance
 */
export const calculateMortgageBalance = (mortgageAmount, annualInterestRate, amortizationYears, paymentFrequency, paymentsMade) => {
  if (!mortgageAmount || mortgageAmount <= 0) {
    return 0;
  }

  if (paymentsMade <= 0) {
    return roundToCent(mortgageAmount);
  }

  // Get payment calculation parameters
  const { periodicPayment, periodicRate, totalPayments } = calculateMortgagePayment(
    mortgageAmount,
    annualInterestRate,
    amortizationYears,
    paymentFrequency
  );

  if (periodicPayment <= 0) {
    return roundToCent(mortgageAmount);
  }

  // Bank calculators use the payment rounded to the cent for the schedule
  const roundedPeriodicPayment = roundToCent(periodicPayment);

  // If all payments are made, balance is 0
  if (paymentsMade >= totalPayments) {
    return 0;
  }

  // Simulate payments up to paymentsMade
  let currentBalance = roundToCent(mortgageAmount);

  for (let paymentNum = 1; paymentNum <= paymentsMade && paymentNum <= totalPayments; paymentNum++) {
    if (currentBalance <= 0) {
      break;
    }

    // Calculate interest for this period
    let interestThisPeriod = 0;
    if (periodicRate > 0) {
      interestThisPeriod = roundToCent(currentBalance * periodicRate);
    }

    // Calculate principal payment
    let principalThisPeriod = 0;
    let actualPayment = roundedPeriodicPayment;

    // For the last payment, adjust to not exceed balance
    if (
      paymentNum === totalPayments ||
      roundToCent(currentBalance + interestThisPeriod) <= roundedPeriodicPayment
    ) {
      actualPayment = roundToCent(currentBalance + interestThisPeriod);
      principalThisPeriod = roundToCent(currentBalance);
      currentBalance = 0;
    } else {
      principalThisPeriod = roundToCent(actualPayment - interestThisPeriod);
      currentBalance = roundToCent(currentBalance - principalThisPeriod);
    }

    // Ensure balance doesn't go negative
    if (currentBalance < 0) {
      currentBalance = 0;
    }
  }

  return roundToCent(currentBalance);
};

/**
 * Calculate number of payments made after a given number of years
 * 
 * @param {number} years - Number of years (0 = year 0/purchase, 1 = first full year, etc.)
 * @param {string} paymentFrequency - 'monthly', 'biweekly', or 'weekly'
 * @returns {number} - Number of payments made
 */
export const getPaymentsMadeAfterYears = (years, paymentFrequency = 'monthly') => {
  let paymentsPerYear;
  switch (paymentFrequency) {
    case 'weekly':
      paymentsPerYear = 52;
      break;
    case 'biweekly':
      paymentsPerYear = 26;
      break;
    case 'monthly':
    default:
      paymentsPerYear = 12;
      break;
  }
  
  // Year 0 = 0 payments, Year 1 = paymentsPerYear payments, Year 2 = 2 * paymentsPerYear, etc.
  return years * paymentsPerYear;
};

/**
 * Generate amortization schedule by simulating each payment period by period
 * This matches bank calculators exactly by rounding at each step
 * 
 * @param {number} mortgageAmount - Initial mortgage amount
 * @param {number} annualInterestRate - Annual interest rate as percentage (e.g., 5.5)
 * @param {number} amortizationYears - Amortization period in years
 * @param {string} paymentFrequency - 'monthly', 'biweekly', or 'weekly'
 * @returns {Array} - Array of yearly summaries: [{ year, interestPaid, principalPaid, balance }]
 */
export const generateAmortizationSchedule = (mortgageAmount, annualInterestRate, amortizationYears, paymentFrequency = 'monthly') => {
  if (!mortgageAmount || mortgageAmount <= 0) {
    return [];
  }

  // Get payment calculation parameters
  const { periodicPayment, periodicRate, paymentsPerYear, totalPayments } = calculateMortgagePayment(
    mortgageAmount,
    annualInterestRate,
    amortizationYears,
    paymentFrequency
  );

  if (periodicPayment <= 0) {
    return [];
  }

  // Use the payment rounded to the cent (bank-calculator style)
  const roundedPeriodicPayment = roundToCent(periodicPayment);

  // Simulate each payment period by period
  let currentBalance = roundToCent(mortgageAmount);
  const schedule = [];
  let currentYear = 1;
  let yearInterestPaid = 0;
  let yearPrincipalPaid = 0;
  let paymentCount = 0;

  for (let paymentNum = 1; paymentNum <= totalPayments; paymentNum++) {
    if (currentBalance <= 0) {
      break;
    }

    // Calculate interest for this period (on current balance)
    let interestThisPeriod = 0;
    if (periodicRate > 0) {
      interestThisPeriod = roundToCent(currentBalance * periodicRate);
    }

    // Calculate principal payment
    let principalThisPeriod = 0;
    let actualPayment = roundedPeriodicPayment;

    // For the last payment, adjust to not exceed balance
    if (
      paymentNum === totalPayments ||
      roundToCent(currentBalance + interestThisPeriod) <= roundedPeriodicPayment
    ) {
      // Last payment: pay remaining balance + interest
      actualPayment = roundToCent(currentBalance + interestThisPeriod);
      principalThisPeriod = roundToCent(currentBalance);
      currentBalance = 0;
    } else {
      // Regular payment: principal = payment - interest
      principalThisPeriod = roundToCent(actualPayment - interestThisPeriod);
      currentBalance = roundToCent(currentBalance - principalThisPeriod);
    }

    // Ensure balance doesn't go negative
    if (currentBalance < 0) {
      principalThisPeriod = roundToCent(principalThisPeriod + currentBalance);
      currentBalance = 0;
    }

    // Accumulate for current year
    yearInterestPaid = roundToCent(yearInterestPaid + interestThisPeriod);
    yearPrincipalPaid = roundToCent(yearPrincipalPaid + principalThisPeriod);
    paymentCount++;

    // When we've made all payments for this year, save the year summary
    if (paymentCount >= paymentsPerYear || paymentNum === totalPayments || currentBalance <= 0) {
      schedule.push({
        year: currentYear,
        interestPaid: roundToCent(yearInterestPaid),
        principalPaid: roundToCent(yearPrincipalPaid),
        balance: roundToCent(currentBalance)
      });

      // Reset for next year
      currentYear++;
      yearInterestPaid = 0;
      yearPrincipalPaid = 0;
      paymentCount = 0;
    }
  }

  return schedule;
};
